import { PagingResponse } from '@/@types/responses/common/PagingResponse'
import { AppConfig } from '@/app.config'
import authenticatedFetch from '@/authenticatedFetch'
import { ServiceDetails } from '@/@types/responses/service/ServiceDetails'
import { Service } from '@/@types/responses/service/Service'
import { MiscellaneousService } from './miscellaneous.service'
import { CreateServiceMessage } from '@/@types/messages/CreateServiceMessage'
import { UpdateServiceMessage } from '@/@types/messages/UpdateServiceMessage'
import { CreateServiceFieldMessage } from '@/@types/messages/CreateServiceFieldMessage'
import { ObjectCreatedResponse } from '@/@types/responses/common/ObjectCreatedResponse'

export class ServiceService {
  private appConfig: AppConfig
  private miscellaneousService: MiscellaneousService

  constructor(miscellaneousService: MiscellaneousService) {
    this.appConfig = new AppConfig()
    this.miscellaneousService = miscellaneousService
  }

  public createService(createServiceMessage: CreateServiceMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createServiceMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public createServiceField(serviceId: number, createServiceFieldMessage: CreateServiceFieldMessage): Promise<ObjectCreatedResponse<number>> {
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(createServiceFieldMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services/${serviceId}/service-fields`, this.miscellaneousService, requestOptions).then(async response => {
      const json = await response.json()
      return new ObjectCreatedResponse<number>(json.id)
    })
  }

  public deleteService(id: number): Promise<void> {
    const requestOptions = {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services/${id}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }

  public getService(id: number): Promise<ServiceDetails> {
    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services/${id}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return new ServiceDetails(json.id, json.allowDiscounts, json.description, json.name, json.price, json.sku, json.buildStepId, json.serviceFields, json.createdByUser, json.createdDate, json.lastModifiedByUser, json.lastModifiedDate)
    })
  }

  public searchServices(searchTerm: string, pageNumber: number, pageSize: number, sortField: string, buildStepId: number|undefined, ascending: boolean): Promise<PagingResponse<Service>> {
    let queryString = `?searchTerm=${searchTerm}&pageNumber=${pageNumber}&pageSize=${pageSize}&sortField=${sortField}&ascending=${ascending}`

    if (buildStepId)
      queryString += `&buildStepId=${buildStepId}`

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services${queryString}`, this.miscellaneousService).then(async response => {
      const json = await response.json()
      return json as PagingResponse<Service>
    })
  }

  public updateService(serviceId: number, updateServiceMessage: UpdateServiceMessage): Promise<void> {
    const requestOptions = {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updateServiceMessage)
    }

    return authenticatedFetch(`${this.appConfig.sacServicesUrl}/v1/services/${serviceId}`, this.miscellaneousService, requestOptions).then(() => {
      return Promise.resolve()
    })
  }
}
